import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, AlertIcon, Box, Button, ButtonGroup, HStack, IconButton, Spinner, Text, Tooltip, useToast } from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import Navbar from "./Navbar";
import WatermarkBackground from "./components/WatermarkBackground";
import { usePlacingBook } from "./hooks/usePlacingBook";
import { showApiErrorToast } from "./utils/showApiToast";
import BrokerPlacingPortal from "./components/placing/BrokerPlacingPortal";
import CarrierPlacingInbox from "./components/placing/CarrierPlacingInbox";

/**
 * The placing landing page. A broker sees every placement their
 * organisations run; a carrier sees what has been sent to their parties.
 * Someone who is both can switch between the two.
 */
const PlacingPortalPage = () => {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading, refreshing, lastUpdated, error, records, summaries, mine, dealsById, productNames, reload } = usePlacingBook();

  useEffect(() => {
    // Only on the first load: a failed background refresh keeps the last data
    if (error && !records) showApiErrorToast(toast, error, { title: "Couldn't load placing" });
  }, [error, records, toast]);

  const received = records
    ? [records.submissions, records.quoteRequests, records.signedLineAdvices, records.lineRequests]
        .flat()
        .filter((r) => r && !mine(r))
    : [];
  const isBroker = summaries.length > 0;
  const isCarrier = received.length > 0;
  // The carrier's own parties: their entries on submissions sent to them
  // (the API returns only those) and the receivers of their quote requests
  const myPartyIds = [
    ...new Set(
      [
        ...(records?.submissions || []).filter((s) => !mine(s)).flatMap((s) => (s.sharedWithParties || []).map((l) => l.partyId)),
        ...(records?.quoteRequests || []).filter((q) => !mine(q)).map((q) => q.receiver?.partyId),
      ].filter(Boolean)
    ),
  ];

  const requested = searchParams.get("view") || (searchParams.get("tab") === "inbox" ? "carrier" : null);
  const view = requested === "carrier" || (!isBroker && isCarrier && requested !== "broker") ? "carrier" : "broker";

  return (
    <Box position="relative" minHeight="100vh">
      <WatermarkBackground />
      <Navbar />
      <Box p={6} maxW="1500px" mx="auto">
        {loading ? (
          <Spinner />
        ) : error && !records ? (
          <Alert status="error"><AlertIcon />{error.message || "Couldn't load placing."}</Alert>
        ) : (
          <>
            <HStack justify="flex-end" mb={2} spacing={3}>
              <Tooltip label="Refreshes every minute while this page is open" hasArrow>
                <Text fontSize="xs" color={error ? "red.500" : "gray.500"}>
                  {error ? "Couldn't refresh · showing " : "Updated "}
                  {lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                </Text>
              </Tooltip>
              <IconButton size="xs" variant="ghost" aria-label="Refresh now" icon={<RepeatIcon />} onClick={reload} isLoading={refreshing} />
              {isBroker && isCarrier && (
                <ButtonGroup size="xs" isAttached variant="outline">
                  <Button isActive={view === "broker"} onClick={() => setSearchParams({ view: "broker" })}>As broker</Button>
                  <Button isActive={view === "carrier"} onClick={() => setSearchParams({ view: "carrier" })}>As carrier</Button>
                </ButtonGroup>
              )}
            </HStack>
            {view === "carrier" ? (
              <CarrierPlacingInbox records={records} mine={mine} myPartyIds={myPartyIds} />
            ) : (
              <BrokerPlacingPortal summaries={summaries} dealsById={dealsById} productNames={productNames} />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default PlacingPortalPage;
